import { Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import {
  OverrideModalService,
  OverrideHistoryParams,
  SystemValueParams,
  SaveOverrideParams
} from './override-modal.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-efile-override-modal',
    templateUrl: './override-modal.component.html',
    styleUrls: ['./override-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class OverrideModalComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  /**
   * Caller-provided row payload. When set, takes precedence over the
   * workspace-driven `rowPayloadFromActiveScreen()` lookup. This lets the
   * migrated overrides grid (`<gtw-efile-overrides>`) open the modal
   * inline with the row from a `(gridCallback)` event — the legacy flow
   * `overridesController.ctrlFn` -> `$uibModal.open({resolve: rowData})`.
   *
   * Workspace-driven init still runs (so `baseURLs`, `headerData`,
   * `globalService` stay populated), it just no longer clobbers `rowData`.
   */
  @Input('row-data')
  set _rowDataInput(value: any) {
    if (value && typeof value === 'object') {
      this.rowData = value;
      this.hasInputRowData = true;
      this.tryInit();
      this.cdr.markForCheck();
    }
  }

  /**
   * Caller-provided submission context (legacy `EfileFactory.selectedSubmission`).
   * Same precedence rule as `rowData`.
   */
  @Input('selected-submission')
  set _selectedSubmissionInput(value: any) {
    if (value && typeof value === 'object') {
      this.selectedSubmission = value;
      this.hasInputSelectedSubmission = true;
      this.cdr.markForCheck();
    }
  }

  private subs = new Subscription();
  private isInitialized = false;
  private hasInputRowData = false;
  private hasInputSelectedSubmission = false;

  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: ''
  };
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  filterParams: any = {};
  headerData: any = {};
  selectedSubmission: any = {};

  minimise = false;
  manual_value = '';
  system_value = '';
  amount_type: 'manual' | 'override' | null = null;
  amountType: 'manual' | 'override' | null = null;
  warningMsg = '';
  crudLoading = false;
  isSaving = false;
  isLocked = false;
  overrideValuesSpinner = false;
  overideHisTableData: { header: string[]; body: any[] } = {
    header: ['Active', 'Element Value', 'Date Created', 'Created By', 'Source Type'],
    body: []
  };
  version: number | null = null;
  expandedValueIndex: { [key: number]: boolean } = {};

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private overrideModalService: OverrideModalService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext()));
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext()));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    this.headerData = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.USER_SETTINGS = this.headerData.userSettingsData;
    this.GENERAL_CONFIG = this.headerData.generalConfigData;
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    if (!this.hasInputSelectedSubmission) {
      this.selectedSubmission = (activeScreen.data as any)?.selectedSubmissionData || {};
    }
    if (!this.hasInputRowData) {
      this.rowData = rowPayloadFromActiveScreen(activeScreen);
    }
    this.filterParams = normalizeFilterParams(activeScreen);
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    if (!this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    // Legacy `EfileFactory.getSysOverideHis` (EfileService.js L574) targets
    // the workspace base URL (`baseURLs.api`), NOT the efile microservice —
    // we only need `api` to be set before issuing the loadJsonObject calls.
    if (!this.baseURLs.api) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.loadOverrideHistory();
    this.cdr.detectChanges();
  }

  private loadOverrideHistory(): void {
    this.overrideValuesSpinner = true;
    this.cdr.detectChanges();

    const params: OverrideHistoryParams = {
      tax_year: this.rowData.TAX_YEAR,
      job_id: this.rowData.JOB_ID,
      key_id: this.rowData.KEY_ID,
      element_name: this.rowData.ELEMENT_NAME,
      sno: this.rowData.SNO,
      form_sub: this.rowData.FORM_SUB || '',
      parent_element_name: this.rowData.PARENT_ELEMENT_NAME || '',
      submission_id: this.rowData.SUBMISSION_ID
    };

    this.overrideModalService.getOverrideHistory(this.baseURLs.api, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data) => {
        if (data.callSuccess === '1') {
          this.overideHisTableData.body = data.jsonObject || [];
          this.loadSystemValue();
          this.loadSubmissionLocked();
        }
        this.overrideValuesSpinner = false;
        this.cdr.detectChanges();
      },
      () => {
        this.overrideValuesSpinner = false;
        this.cdr.detectChanges();
      }
    );
  }

  private loadSystemValue(): void {
    const params: SystemValueParams = {
      tax_year: this.rowData.TAX_YEAR,
      job_id: this.rowData.JOB_ID,
      key_id: this.rowData.KEY_ID,
      element_name: this.rowData.ELEMENT_NAME,
      sno: this.rowData.SNO,
      form_sub: this.rowData.FORM_SUB || '',
      parent_element_name: this.rowData.PARENT_ELEMENT_NAME || ''
    };

    this.overrideModalService.getSystemValue(this.baseURLs.api, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data) => {
        if (data.callSuccess === '1') {
          this.system_value = (data.jsonObject && data.jsonObject.length > 0)
            ? data.jsonObject[0].VALUE
            : '';
          const activeValue = this.overideHisTableData.body.find((b: any) => b.IS_ACTIVE === 'True');
          if (activeValue && !this.isEmpty(activeValue.ELEMENT_VALUE)) {
            this.manual_value = activeValue.ELEMENT_VALUE;
            this.amount_type = 'manual';
            this.amountType = 'manual';
            this.version = activeValue.VERSION;
          }
        }
        this.cdr.detectChanges();
      }
    );
  }

  private loadSubmissionLocked(): void {
    this.overrideModalService.getSubmissionLocked(
      this.baseURLs.api,
      this.rowData.SUBMISSION_ID
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data) => {
        if (data.callSuccess === '1') {
          this.isLocked = data.jsonObject?.[0]?.LOCK_IND === 1;
          if (this.isLocked) {
            this.gtwSnackbarService.openSnackBar(
              'The selected submission is already submitted to IRS or locked',
              'gtw-snackbar--yellow'
            );
          }
        }
        this.overrideValuesSpinner = false;
        this.cdr.detectChanges();
      },
      () => {
        this.overrideValuesSpinner = false;
        this.cdr.detectChanges();
      }
    );
  }

  private isEmpty(val: any): boolean {
    return val === undefined || val === null || val === '';
  }

  /** Manual vs system-override radios — explicit checked + change (OnPush-safe). */
  setAmountType(type: 'manual' | 'override'): void {
    if (type === 'override' && (this.system_value === '' || this.isLocked)) {
      return;
    }
    if (type === 'manual' && this.isLocked) {
      return;
    }
    this.amount_type = type;
    this.amountType = type;
    this.warningMsg = '';
    this.getVal();
    this.cdr.markForCheck();
  }

  isHistoryRowActive(item: any): boolean {
    const v = item?.IS_ACTIVE;
    if (v === true || v === 1) {
      return true;
    }
    return String(v ?? '').toLowerCase() === 'true';
  }

  getVal(): void {
    this.amountType = this.amount_type;
    if (this.amountType === 'override') {
      this.overideHisTableData.body.forEach((i: any) => { i.IS_ACTIVE = 'False'; });
    } else {
      this.overideHisTableData.body.forEach((i: any) => {
        if (this.version === i.VERSION) {
          i.IS_ACTIVE = 'True';
        }
      });
    }
    this.cdr.detectChanges();
  }

  getActiveVal(item: any, index: number): void {
    this.overideHisTableData.body.forEach((ob: any, i: number) => {
      if (i === index && ob.ELEMENT_VALUE === item.ELEMENT_VALUE) {
        ob.IS_ACTIVE = 'True';
        this.manual_value = item.ELEMENT_VALUE;
        this.version = item.VERSION;
        this.amount_type = 'manual';
        this.amountType = 'manual';
      } else {
        ob.IS_ACTIVE = 'False';
      }
    });
    this.cdr.detectChanges();
  }

  toggleLoadMore(index: number): void {
    this.expandedValueIndex[index] = !this.expandedValueIndex[index];
    this.cdr.detectChanges();
  }

  isLoadMoreExpanded(index: number): boolean {
    return !!this.expandedValueIndex[index];
  }

  getDisplayValue(item: any, index: number): string {
    const val = item.ELEMENT_VALUE || '';
    const expanded = this.isLoadMoreExpanded(index);
    return expanded || val.length < 50 ? val : val.substring(0, 50);
  }

  save(): void {
    if (this.amountType === null) {
      this.warningMsg = 'Please choose type of Element Value';
      this.cdr.detectChanges();
      return;
    }
    if (this.isSaving) {
      this.gtwSnackbarService.openSnackBar(
        'Please wait until existing save completes',
        'gtw-snackbar--yellow'
      );
      return;
    }

    const scenario = this.getScenario();
    const clob_data = {
      submissionId: this.selectedSubmission.SUBMISSION_ID || this.rowData.SUBMISSION_ID,
      processName: 'SAVE_EFILE_ELEMENT_OVERRIDE',
      keyId: this.rowData.KEY_ID,
      form: this.rowData.FORM,
      formSub: this.rowData.FORM_SUB,
      pen: this.rowData.PARENT_ELEMENT_NAME,
      formId: this.rowData.FORM_ID,
      parentSno: '0',
      job_id: this.rowData.JOB_ID,
      key_id: this.rowData.KEY_ID,
      changes: {
        elementName: this.rowData.ELEMENT_NAME,
        sno: this.rowData.SNO,
        elementValue: this.amountType === 'manual'
          ? this.manual_value
          : (this.system_value === '' ? null : this.system_value),
        def_to_sys: this.amountType === 'manual' ? 'N' : 'Y',
        active_version: this.amountType === 'manual' ? (this.version ?? 0) : 0
      }
    };

    const saveParams: SaveOverrideParams = {
      clob_settings: JSON.stringify({
        tax_year: this.selectedSubmission.TAX_YEAR || this.rowData.TAX_YEAR,
        scenario,
        jcd_key: 250
      }),
      tax_year: this.selectedSubmission.TAX_YEAR || this.rowData.TAX_YEAR,
      scenario,
      jcd_key: 250,
      clob_data: JSON.stringify(clob_data),
      sso_id: this.USER_SETTINGS?.user?.sso_id || ''
    };

    this.crudLoading = true;
    this.isSaving = true;
    this.cdr.detectChanges();

    this.overrideModalService.saveOverride(this.baseURLs.api, saveParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response) => {
        this.crudLoading = false;
        this.isSaving = false;
        if (response.callSuccess === '0') {
          this.gtwSnackbarService.openSnackBar(
            'Something went wrong while saving the data!',
            'gtw-snackbar--red'
          );
        } else {
          this.gtwSnackbarService.openSnackBar(
            'Data saved successfully!',
            'gtw-snackbar--green'
          );
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      () => {
        this.crudLoading = false;
        this.isSaving = false;
        this.gtwSnackbarService.openSnackBar(
          'Something went wrong while saving the data!',
          'gtw-snackbar--red'
        );
        this.cdr.detectChanges();
      }
    );
  }

  private getScenario(): string | number {
    const taxYearVal = this.selectedSubmission.TAX_YEAR ?? this.rowData.TAX_YEAR;
    // `this.globalService` is only `hydratedGlobalParams()` (globalParams + filters) — it
    // does not include `inputs`. Tax-year → scenario catalog lives on the real GlobalService.
    const values = this.gtwGlobal?.global?.inputs?.tax_year?.values || [];
    const tyStr = String(taxYearVal ?? '').trim();
    const found = values.find(
      (v: any) => String(v?.value ?? '').trim() === tyStr,
    );
    if (found != null && found.scenario != null && found.scenario !== '') {
      return found.scenario;
    }
    const fromContext =
      this.gtwGlobal?.global?.globalParams?.scenario ??
      this.filterParams?.scenario ??
      this.filterParams?.SCENARIO;
    if (fromContext != null && fromContext !== '') {
      return fromContext;
    }
    const viaService = this.gtwGlobal.getScenario?.(taxYearVal);
    return viaService != null && viaService !== '' ? viaService : 0;
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
